import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Flex } from '$components/layouts';
import { useTranslation } from '$hooks';
import { formatString } from '$lib/helpers';
import { SearchContentTypes } from '~/modules/search';
import { key } from '~/utils/key';
import { IFullSearchResult } from '$models';
import { Loading } from '$components/elements/loading';
import { IHelloRetailFilterSelections, IHelloRetailSearchResponse } from '~/modules/search/interfaces/suggestion';
import { useHelloRetailSearch } from '../../hooks/combined-search/use-hello-retail-search';
import { HelloRetailProductList } from '../hello-retail/hello-retail-product-list';
import { CombinedSearchHeader } from './combined-search-header';
import { BlogPostCard } from '../hello-retail/result-types/blog-post-card';
import { useLocalSearch } from '../../hooks/combined-search/use-local-search';
import { ResultList } from '../hello-retail/result-list';
import { StoreCard } from '../hello-retail/result-types/store-card';
import { SearchResultList } from '../hello-retail/search-results-list';
import { ProductCarouselSpot } from '$components/spots/product-carousel-spot';
import { CategoryList } from '../hello-retail/category-list';

export type CombinedSearchModuleProps = {
    searchQuery?: string;
    mode?: SearchContentTypes;
    initialData?: IFullSearchResult[];
    initialSortOrderKey?: string;
    initialHelloRetailData?: IHelloRetailSearchResponse[];
};

/* Combines hello retails search with out own */

export const CombinedSearchModule: FC<CombinedSearchModuleProps> = ({
    searchQuery = '',
    mode,
    initialData,
    // initialSortOrderKey,
    initialHelloRetailData,
}) => {
    const { translate } = useTranslation();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [userInteracted, setUserInteracted] = useState(false);
    const [selectedMode, setSelectedMode] = useState(mode || SearchContentTypes.All);
    const [prevQuery, setPrevQuery] = useState<string>();
    const [prevMode, setPrevMode] = useState<SearchContentTypes>();
    const [initialCount, setInitialCount] = useState<number>();
    const [isReady, setIsReady] = useState(false);
    const [oldDataKeyHelloRetail, setOldDataKeyHelloRetail] = useState<string>('');
    const [oldDataKeyLocal, setOldDataKeyLocal] = useState<string>('');

    const productResultRef = useRef<HTMLDivElement>(null);
    const authorResultRef = useRef<HTMLDivElement>(null);
    const serieResultRef = useRef<HTMLDivElement>(null);
    const blogPostResultRef = useRef<HTMLDivElement>(null);
    const sitePageResultRef = useRef<HTMLDivElement>(null);
    const categoryResultRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setIsReady(true);
    }, []);

    useEffect(() => {
        let count = 0;
        count += initialData?.[0]?.authors?.totalDocumentsFound || 0;
        count += initialData?.[0]?.series?.totalDocumentsFound || 0;
        count += initialHelloRetailData?.[0]?.blogPosts?.totalCount || 0;
        count += initialHelloRetailData?.[0]?.categories?.totalCount || 0;
        count += initialHelloRetailData?.[0]?.products?.totalCount || 0;
        count += initialHelloRetailData?.[0]?.sitePages?.totalCount || 0;
        setInitialCount(count);
    }, [initialData, initialHelloRetailData]);

    useEffect(() => {
        if (searchQuery !== prevQuery) {
            setIsLoading(true);
        }
        if ((prevQuery && searchQuery !== prevQuery) || (prevMode && mode !== prevMode)) {
            setUserInteracted(false);
            setSelectedMode(mode || SearchContentTypes.All);
        }
        if (searchQuery) {
            setPrevQuery(searchQuery);
        }
        if (mode) {
            setPrevMode(mode);
        }
    }, [searchQuery, mode]);

    const {
        setFacets,
        searchTerm,
        productResults,
        productTotalCount,
        categoryResults,
        categoryTotalCount,
        blogPostResults,
        blogPostTotalCount,
        storeResults,
        storeTotalCount,
        productZeroResults,
        productZeroResultsTitle,
        loading,
        loadingPage,
        loadNextPage,
        productFacets,
        productSorting: sortingOptions,
        searchWordResults,
        dataKey: helloRetailDataKey,
    } = useHelloRetailSearch(
        searchQuery,
        mode || SearchContentTypes.All,
        initialHelloRetailData,
        isReady && prevQuery === searchQuery && prevMode === mode,
        userInteracted
    );

    const {
        authorResults,
        authorTotalCount,
        serieResults,
        seriesTotalCount,
        loadNextPage: loadNextPageLocal,
        loading: loadingLocal,
        loadingPage: loadingLocalPage,
        dataKey: localDataKey,
    } = useLocalSearch(
        searchQuery,
        mode || SearchContentTypes.All,
        initialData,
        isReady && prevQuery === searchQuery && prevMode === mode,
        userInteracted
    );

    // Set loading to false on every result update.
    useEffect(() => {
        if (oldDataKeyHelloRetail !== helloRetailDataKey) {
            setOldDataKeyHelloRetail(helloRetailDataKey);
            setIsLoading(false);
        }
    }, [helloRetailDataKey]);
    // Set loading to false on every result update.
    useEffect(() => {
        if (oldDataKeyLocal !== localDataKey) {
            setOldDataKeyLocal(localDataKey);
            setIsLoading(false);
        }
    }, [localDataKey]);

    const onSetFacets = (newFacets: IHelloRetailFilterSelections) => {
        setIsLoading(true);
        setUserInteracted(true);
        setFacets(newFacets);
        setSelectedMode(SearchContentTypes.Products);
    };

    const onLoadMore = (type: SearchContentTypes, isLocal?: boolean) => {
        setUserInteracted(true);
        setSelectedMode(type);
        if (isLocal) {
            loadNextPageLocal(type);
        } else {
            loadNextPage(type);
        }
    };

    const hasNoResults = useMemo(() => {
        if (!selectedMode || selectedMode === SearchContentTypes.All) {
            return (
                !isLoading &&
                !loading &&
                !loadingLocal &&
                !productResults?.length &&
                !serieResults?.series?.length &&
                !blogPostResults?.length &&
                !categoryResults?.length
            );
        }

        if (selectedMode === SearchContentTypes.Products) {
            return !productResults?.length;
        }
        if (selectedMode === SearchContentTypes.Series) {
            return !serieResults?.series?.length;
        }
        if (selectedMode === SearchContentTypes.BlogPost) {
            return !blogPostResults?.length;
        }
        if (selectedMode === SearchContentTypes.Categories) {
            return !categoryResults?.length;
        }
    }, [
        isLoading,
        loading,
        loadingLocal,
        productResults,
        serieResults,
        blogPostResults,
        categoryResults,
        selectedMode,
    ]);

    return (
        <SearchModuleWrapper column>
            {isLoading && loading && loadingLocal && <Loading showBackdrop />}
            <SearchResults>
                <CombinedSearchHeader
                    hasTypeSelection={selectedMode !== SearchContentTypes.All}
                    currentType={selectedMode}
                    searchTerm={searchTerm}
                    searchWordSuggestions={searchWordResults}
                    totalResults={initialCount}
                    tabCount={{
                        authors: authorTotalCount || 0,
                        products: productTotalCount || 0,
                        series: seriesTotalCount || 0,
                        blogPost: blogPostTotalCount || 0,
                        sitePages: storeTotalCount || 0,
                        categories: categoryTotalCount || 0,
                    }}
                    resultListRefs={{
                        authors: authorResultRef,
                        products: productResultRef,
                        series: serieResultRef,
                        blogPost: blogPostResultRef,
                        sitePages: sitePageResultRef,
                        categories: categoryResultRef,
                    }}
                    hasNoResults={hasNoResults}
                />
                {/* Products */}
                {(selectedMode === SearchContentTypes.All || selectedMode === SearchContentTypes.Products) && (
                    <SearchResultSection ref={productResultRef}>
                        <HelloRetailProductList
                            productResult={productResults}
                            onLoadNextPage={() => onLoadMore(SearchContentTypes.Products)}
                            onSelectedFacets={onSetFacets}
                            facets={productFacets}
                            total={productTotalCount}
                            loadingPage={loadingPage}
                            sortingOptions={sortingOptions}
                            userInteracted={userInteracted}
                        />
                    </SearchResultSection>
                )}
                {/* Categories */}
                {!!categoryResults?.length &&
                    (selectedMode === SearchContentTypes.All || selectedMode === SearchContentTypes.Categories) && (
                        <SearchResultSection ref={categoryResultRef}>
                            <CategoryList
                                categories={categoryResults}
                                categoryTotalCount={categoryTotalCount}
                                currentResults={categoryResults.length}
                                isLoadingMore={loadingPage}
                                onLoadMore={() => onLoadMore(SearchContentTypes.Categories)}
                                hasNextPage={categoryTotalCount > (categoryResults.length || 0)}
                                isIsolatedResult={selectedMode === SearchContentTypes.Categories}
                                userInteracted={userInteracted}
                            />
                        </SearchResultSection>
                    )}
                {/* Series */}
                {!!serieResults?.series?.length &&
                    (selectedMode === SearchContentTypes.All || selectedMode === SearchContentTypes.Series) && (
                        <SearchResultSection ref={serieResultRef}>
                            <SearchResultList
                                series={serieResults}
                                title={formatString(translate('search.searchResultPage.series'), seriesTotalCount || 0)}
                                onLoadMore={() => onLoadMore(SearchContentTypes.Series, true)}
                                isLoadingMore={loadingLocalPage}
                                currentResults={serieResults?.series?.length || 0}
                                totalResults={seriesTotalCount || 0}
                                userInteracted={userInteracted}
                                isIsolatedResult={selectedMode === SearchContentTypes.Series}
                            />
                        </SearchResultSection>
                    )}
                {/* Authors */}
                {!!authorResults?.authors?.length &&
                    (selectedMode === SearchContentTypes.All || selectedMode === SearchContentTypes.Authors) && (
                        <SearchResultSection ref={authorResultRef}>
                            <SearchResultList
                                authors={authorResults}
                                title={formatString(
                                    translate('search.searchResultPage.persons'),
                                    authorTotalCount || 0
                                )}
                                onLoadMore={() => onLoadMore(SearchContentTypes.Authors, true)}
                                isLoadingMore={loadingLocalPage}
                                currentResults={authorResults?.authors?.length || 0}
                                totalResults={authorTotalCount || 0}
                                userInteracted={userInteracted}
                                isIsolatedResult={selectedMode === SearchContentTypes.Authors}
                            />
                        </SearchResultSection>
                    )}
                {/* Articles */}
                {!!blogPostResults?.length &&
                    (selectedMode === SearchContentTypes.All || selectedMode === SearchContentTypes.BlogPost) && (
                        <SearchResultSection ref={blogPostResultRef}>
                            <ResultList
                                title={formatString(
                                    translate('search.searchResultPage.articles'),
                                    blogPostTotalCount || 0
                                )}
                                onLoadMore={() => onLoadMore(SearchContentTypes.BlogPost)}
                                isLoadingMore={loadingPage}
                                currentResults={blogPostResults?.length || 0}
                                totalResults={blogPostTotalCount || 0}
                                hasNextPage={blogPostTotalCount > (blogPostResults?.length || 0)}
                                userInteracted={userInteracted}
                                isIsolatedResult={selectedMode === SearchContentTypes.BlogPost}
                            >
                                {blogPostResults?.map((blogPost) => (
                                    <BlogPostCard key={key(blogPost)} blogPost={blogPost} />
                                ))}
                            </ResultList>
                        </SearchResultSection>
                    )}
                {/* Stores */}
                {!!storeResults?.length &&
                    (selectedMode === SearchContentTypes.All || selectedMode === SearchContentTypes.Store) && (
                        <SearchResultSection ref={sitePageResultRef}>
                            <ResultList
                                title={formatString(translate('search.searchResultPage.stores'), storeTotalCount || 0)}
                                onLoadMore={() => onLoadMore(SearchContentTypes.Store)}
                                isLoadingMore={loadingPage}
                                currentResults={storeResults?.length || 0}
                                totalResults={storeTotalCount || 0}
                                hasNextPage={storeTotalCount > (storeResults?.length || 0)}
                                userInteracted={userInteracted}
                                isIsolatedResult={selectedMode === SearchContentTypes.Store}
                            >
                                {storeResults?.map((store) => (
                                    <StoreCard key={key(store)} store={store} />
                                ))}
                            </ResultList>
                        </SearchResultSection>
                    )}
                {hasNoResults && !!productZeroResults?.length && productZeroResultsTitle && (
                    <ProductCarouselSpot
                        products={productZeroResults}
                        heading={productZeroResultsTitle}
                        subHeading=""
                        ignoreVisibilityClass
                    />
                )}
            </SearchResults>
        </SearchModuleWrapper>
    );
};

const SearchModuleWrapper = styled(Flex)({});

const SearchResults = styled.div({});

const SearchResultSection = styled.div(({ theme }) => ({
    marginBottom: theme.space[11],
}));
